import _ from 'underscore';
import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useId } from '@fluentui/react-hooks';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  Link,
  IconButton,
  Icon,
  DetailsList,
  DetailsListLayoutMode,
  DetailsRow,
  SelectionMode,
} from '@fluentui/react';

import { LocationTargetsGridRow } from './location-targets-grid-row';
import { getStyles } from './get-styles';
import { LocationTarget } from '../util/index';
import { BingMapsContext } from '../bing-maps-context/index';

export function LocationTargetsGrid(props) {
  const {
    isNegativeLocationTargetsGrid,
    locationTargets,
    onLocationTargetsChange,
    onLocationTargetEditClick,
    onLocationTargetBlur,
    onLocationTargetFocus,
    onGetErrorMessage,
    onLocationTargetGetErrorMessage,
    hideRemove,
    isLoading,
  } = props;

  const { logActivity } = useContext(BingMapsContext);
  const i18n = useContext(I18nContext);
  const headerText = isNegativeLocationTargetsGrid ? _TL_('Excluded locations') : _TL_('Targeted locations');
  const removeAllButtonAriaLabel = isNegativeLocationTargetsGrid ? _TL_('Remove all excluded locations') : _TL_('Remove all targeted locations');
  const removedAllButtonAriaLabel = isNegativeLocationTargetsGrid ? _TL_('Removed all excluded locations') : _TL_('Removed all targeted locations');
  const columns = [
    {
      key: 'nameAndRadius',
      name: 'Location',
      fieldName: 'ExpandedName',
      minWidth: 100,
      isResizable: false,
    },
    {
      key: 'editButton',
      name: 'Edit',
      fieldName: 'value',
      minWidth: 15,
      maxWidth: 15,
      isResizable: false,
    },
    {
      key: 'removeButton',
      name: 'Remove',
      fieldName: 'value',
      minWidth: 15,
      maxWidth: 15,
      isResizable: false,
    },
  ];

  const classes = useDefaultStyles(getStyles, props);
  const announceId = useId('a11yAnnounceArea');
  const a11yAnnounceArea = document.getElementById(announceId);
  const [removeAllClickCnt, setRemoveAllClickCnt] = useState(0);

  const handleLocationTargetFocus = useCallback((event, locationTarget) => {
    if (onLocationTargetFocus) {
      return onLocationTargetFocus(event, locationTarget);
    }

    return null;
  }, [onLocationTargetFocus]);

  const handleLocationTargetBlur = useCallback((event) => {
    if (onLocationTargetBlur) {
      return onLocationTargetBlur(event);
    }

    return null;
  }, [onLocationTargetBlur]);

  const handleActiveItemChanged = useCallback((item, index, event) => {
    // this is called when using keyboard navigation inside the grid
    handleLocationTargetFocus(event, item);
  }, [handleLocationTargetFocus]);

  const handleRemoveAll = useCallback(() => {
    onLocationTargetsChange([]);
    if (a11yAnnounceArea) {
      a11yAnnounceArea.textContent = '';
    }
    setRemoveAllClickCnt(removeAllClickCnt + 1);
    logActivity('location target grid remove all locations');
  }, [a11yAnnounceArea, logActivity, onLocationTargetsChange, removeAllClickCnt]);

  useEffect(() => {
    if (a11yAnnounceArea && removeAllClickCnt > 0) {
      a11yAnnounceArea.textContent = removedAllButtonAriaLabel;
    }
  }, [a11yAnnounceArea, removeAllClickCnt, removedAllButtonAriaLabel]);

  const handleRenderRow = useCallback((rowProps) => {
    // renders a grid row with mouseover/mouseout handlers
    const handleRowFocus = ev => handleLocationTargetFocus(ev, rowProps.item);

    return (
      <DetailsRow
        {...rowProps}
        onFocus={handleRowFocus}
        onMouseOver={handleRowFocus}
        onBlur={handleLocationTargetBlur}
        onMouseOut={handleLocationTargetBlur}
      />
    );
  }, [handleLocationTargetBlur, handleLocationTargetFocus]);

  // renders the contents for 3 columns: name, edit button, and delete button
  const renderGridItemColumn = useCallback((locationTarget, _index, column) => {
    if (column.key === 'nameAndRadius') {
      const targetErrorMessage
        = onLocationTargetGetErrorMessage(locationTarget, isNegativeLocationTargetsGrid);

      return (
        <LocationTargetsGridRow
          locationTarget={locationTarget}
          isNegativeLocationTarget={isNegativeLocationTargetsGrid}
        >
          {targetErrorMessage && <div className="location-target-error">{targetErrorMessage}</div>}
        </LocationTargetsGridRow>
      );
    }

    if (column.key === 'editButton') {
      if (_.isFunction(onLocationTargetEditClick)) {
        return (
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            title={i18n.getString(_TL_('Edit'))}
            ariaLabel={i18n.getString(_TL_('Edit'))}
            className={classNames('edit-location-target', 'location-target-grid-row-button')}
            onClick={e => onLocationTargetEditClick(e, locationTarget)}
          />
        );
      }
      return null;
    }

    if (!hideRemove) {
      const newLocationTargets = _.without(locationTargets, locationTarget);

      return (
        <IconButton
          iconProps={{ iconName: 'Delete' }}
          title={i18n.getString(_TL_('Remove'))}
          ariaLabel={i18n.getString(_TL_('Remove'))}
          className={classNames('remove-location-target', 'location-target-grid-row-button')}
          onClick={() => {
            logActivity('location target grid remove a location');
            return onLocationTargetsChange(newLocationTargets);
          }}
        />
      );
    }
    return null;
  }, [
    i18n,
    isNegativeLocationTargetsGrid,
    locationTargets,
    onLocationTargetEditClick,
    logActivity,
    onLocationTargetsChange,
    onLocationTargetGetErrorMessage,
    hideRemove,
  ]);

  const errorMessage = useMemo(
    () => onGetErrorMessage(locationTargets, isNegativeLocationTargetsGrid),
    [locationTargets, onGetErrorMessage, isNegativeLocationTargetsGrid]
  );

  return (
    <div className="location-targets-grid">
      {!_.isEmpty(locationTargets) &&
        <div className="location-targets-grid-header clearfix">
          <h3 className="pull-left">{i18n.getString(headerText)}</h3>
          {!hideRemove && !isLoading &&
            <Link
              className="remove-all-location-targets pull-right"
              onClick={handleRemoveAll}
              aria-label={i18n.getString(removeAllButtonAriaLabel)}
            >
              {i18n.getString(_TL_('Remove all'))}
            </Link>}
        </div>
      }

      {/* for a11y announce do not remove the div below */}
      <div
        id={announceId}
        className={classNames('a11yAnnounceArea-removeAll', classes.announceArea)}
        aria-live="polite"
        aria-atomic="true"
      />

      {
        !_.isEmpty(errorMessage) &&
        <div className="location-targets-grid-error">
          <Icon iconName="Warning" className="location-targets-grid-error-icon" />
          <div className="location-targets-grid-error-message">
            {errorMessage}
          </div>
        </div>
      }

      {
        !_.isEmpty(locationTargets) && !isLoading &&
        <DetailsList
          className="location-targets-grid-details-list"
          isHeaderVisible={false}
          items={locationTargets}
          columns={columns}
          setKey="set"
          layoutMode={DetailsListLayoutMode.justified}
          selectionMode={SelectionMode.none}
          onActiveItemChanged={handleActiveItemChanged}
          onBlur={handleLocationTargetBlur}
          onRenderRow={handleRenderRow}
          onRenderItemColumn={renderGridItemColumn}
        />
      }
    </div>
  );
}

LocationTargetsGrid.propTypes = {
  isNegativeLocationTargetsGrid: PropTypes.bool.isRequired,
  locationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)).isRequired,
  onLocationTargetsChange: PropTypes.func,
  onLocationTargetEditClick: PropTypes.func,
  onLocationTargetBlur: PropTypes.func,
  onLocationTargetFocus: PropTypes.func,
  onGetErrorMessage: PropTypes.func.isRequired,
  onLocationTargetGetErrorMessage: PropTypes.func.isRequired,
  hideRemove: PropTypes.bool,
  isLoading: PropTypes.bool,
};

LocationTargetsGrid.defaultProps = {
  onLocationTargetsChange: _.noop,
  onLocationTargetEditClick: null,
  onLocationTargetBlur: null,
  onLocationTargetFocus: null,
  hideRemove: false,
  isLoading: false,
};
